import React, { useEffect, useState, useMemo } from "react";
import {
  Search, Menu, User, X, ChevronLeft, ChevronRight,
  Calendar, Compass, Home, MessageCircle, Minus, Plus,
  Star, MapPin, Bell, Clock,
} from "lucide-react";
import { Tune } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../../../redux/store";
import { TextField } from "@mui/material";
import { FilledHeartIcon, OutlinedHeartIcon } from "../../../../assets/svg/svg";

import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../../redux/store";

import {
  listenToPublishedCleanings,
  listenToCleaningLocations,
  setFilters,
  clearFilters,
} from "../../../../redux/cleaningSlice";
import { listenToAllCleaningReservations } from "../../../../redux/cleaningReservationSlice";
import { calculateFinalPricesForAllCleaningListings } from "../../../../redux/cleaningFeeSlice";
import { fetchAllCleaningRatingSummaries } from "../../../../redux/cleaningRatingSlice";
import { addToWishlist, fetchWishlist, removeFromWishlist } from "../../../../redux/wishlistSlice";
import { listenToUserNotifications, stopUserNotificationsListener } from "../../../../redux/notificationsSlice";
import { useCurrency } from "@cosmoola/react-cosmoola-js";

import dayjs from "dayjs";
import { cleaningCategories, cleaningAmenities } from "../../../../CATEGORIES/Categories";

const GREEN          = "#16a34a";
const GREEN_DARK     = "#14532d";
const GREEN_LIGHT    = "#bbf7d0";
const GREEN_GRADIENT = "linear-gradient(135deg, #16a34a 0%, #065f46 100%)";
const GREY_BG        = "#ebebeb";
const LUXURY_GREEN   = "linear-gradient(135deg, #16a34a 0%, #d4edda 45%, #111 100%)";

type ActiveSection = "where" | "dates" | "who" | "hours" | "filters" | "menu" | "mobile" | null;
type CardKey       = "where" | "when" | "who" | "search" | null;
type WhenMode      = "dates" | "hours" | "months" | "flex" | "years";

const BEDROOM_AMENITY_IDS = [1, 2, 3, 4, 5, 6];

const CleaningHome: React.FC = () => {
  const dispatch   = useDispatch<AppDispatch>();
  const { convertRaw, symbol } = useCurrency();

  const filters            = useSelector((s: RootState) => s.cleaning.filters);
  const listings           = useSelector((s: RootState) => s.cleaning.listings);
  const allReservations    = useSelector((s: RootState) => s.cleaningReservations.allReservations);
  const blockedListingIds  = useSelector((s: RootState) => s.cleaningReservations.blockedListingIds);
  const availableLocations = useSelector((s: RootState) => s.cleaning.locations);
  const user               = useSelector((s: RootState) => s.auth.user);
  const wishlist           = useSelector((s: RootState) => s.wishlist.cleaning ?? []);
  const notifications      = useSelector((s: RootState) => s.notifications.notifications);
  const isAuthenticated    = Boolean(user?.userId);
  const unreadCount        = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

  const [active, setActive]             = useState<ActiveSection>(null);
  const [isMobile, setIsMobile]         = useState(window.innerWidth < 768);
  const [openCard, setOpenCard]         = useState<CardKey>(null);
  const [locationText, setLocationText] = useState("");
  const [suggestions, setSuggestions]   = useState<any[]>([]);
  const inputRef                        = React.useRef<HTMLInputElement | null>(null);

  // when
  const [whenMode, setWhenMode]           = useState<WhenMode>("dates");
  const [checkIn, setCheckIn]             = useState<Date | null>(null);
  const [checkOut, setCheckOut]           = useState<Date | null>(null);
  const [startHour, setStartHour]         = useState<number | null>(null);
  const [endHour, setEndHour]             = useState<number | null>(null);
  const [monthOffset, setMonthOffset]     = useState(0);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [flexLength, setFlexLength]       = useState<"4h" | "8h" | "Full Day" | null>(null);
  const [years, setYears]                 = useState<number>(1);

  // filters
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [priceRange, setPriceRange]                 = useState({ min: 0, max: 10000 });
  const [taxOn, setTaxOn]                           = useState(true);
  const [selectedBedroomId, setSelectedBedroomId]   = useState<number | null>(null);
  const [selectedAmenityIds, setSelectedAmenityIds] = useState<number[]>([]);
  const [cancellationFilter, setCancellationFilter] = useState<"any" | "free" | "partial" | "system">("any");

  const categoriesScrollRef = React.useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const locationPreview =
    locationText ||
    [filters.address?.city, filters.address?.country].filter(Boolean).join(", ") ||
    "Search destinations";

  const maxListingPrice = useMemo(() => {
    if (!listings.length) return 10000;
    return Math.max(...listings.map((l) => convertRaw(l.rate ?? 0, "EUR")));
  }, [listings, convertRaw]);

  const bedroomPreview = selectedBedroomId
    ? cleaningAmenities.find(a => a.id === selectedBedroomId)?.name ?? "Any size"
    : "Any size";

  // years → auto set checkIn/checkOut
  useEffect(() => {
    if (whenMode !== "years") return;
    setCheckIn(dayjs().toDate());
    setCheckOut(dayjs().add(years, "year").toDate());
  }, [years, whenMode]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (active === "mobile" || active === "menu") document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
  }, [active]);

  useEffect(() => {
    const unsub = listenToPublishedCleanings()(dispatch);
    return () => { if (typeof unsub === "function") unsub(); };
  }, [dispatch]);

  useEffect(() => { dispatch(listenToCleaningLocations()); }, [dispatch]);

  useEffect(() => {
    let unsub: any;
    if (checkIn && checkOut) {
      unsub = dispatch(listenToAllCleaningReservations(
        dayjs(checkIn).format("YYYY-MM-DD"),
        dayjs(checkOut).format("YYYY-MM-DD"),
      ));
    } else {
      unsub = dispatch(listenToAllCleaningReservations());
    }
    return () => { if (typeof unsub === "function") unsub(); };
  }, [checkIn, checkOut, dispatch]);

  useEffect(() => { dispatch(fetchAllCleaningRatingSummaries()); }, [dispatch]);

  useEffect(() => {
    if (user?.userId) dispatch(fetchWishlist({ userId: user.userId, type: "cleaning" }));
  }, [dispatch, user?.userId]);

  useEffect(() => {
    if (user?.userId) {
      dispatch(listenToUserNotifications(user.userId));
      return () => { dispatch(stopUserNotificationsListener()); };
    }
  }, [dispatch, user?.userId]);

  useEffect(() => {
    if (!taxOn) { dispatch({ type: "cleaningServiceFees/clearCleaningFinalPrices" }); return; }
    if (!checkIn || !checkOut) return;
    const days  = dayjs(checkOut).diff(dayjs(checkIn), "day");
    const hours = endHour !== null && startHour !== null ? endHour - startHour : 0;
    if (days <= 0 && hours <= 0) return;
    dispatch(calculateFinalPricesForAllCleaningListings({ totalDays: days, totalHours: hours }));
  }, [taxOn, checkIn, checkOut, startHour, endHour, dispatch]);

  useEffect(() => { localStorage.setItem("cleaning:showTotalPrice", String(taxOn)); }, [taxOn]);

  useEffect(() => {
    if (!window.google || !inputRef.current) return;
    const ac = new window.google.maps.places.Autocomplete(inputRef.current, { types: ["(cities)"] });
    ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      const city    = place.address_components?.find((c: any) => c.types.includes("locality"))?.long_name || null;
      const country = place.address_components?.find((c: any) => c.types.includes("country"))?.long_name || null;
      const lat = place.geometry?.location?.lat() ?? null;
      const lng = place.geometry?.location?.lng() ?? null;
      const viewport = place.geometry?.viewport;
      const bounds = viewport ? {
        neLat: viewport.getNorthEast().lat(), neLng: viewport.getNorthEast().lng(),
        swLat: viewport.getSouthWest().lat(), swLng: viewport.getSouthWest().lng(),
      } : null;
      setLocationText(place.name || "");
      dispatch(setFilters({ address: { city, country, neighborhood: null, latitude: lat, longitude: lng, bounds } }));
      setOpenCard("when"); setActive("dates");
    });
  }, [dispatch]);

  useEffect(() => {
    if (!locationText.trim()) { setSuggestions([]); return; }
    const search = locationText.toLowerCase();
    const seen   = new Set<string>();
    const filtered = availableLocations.filter((loc: any) => {
      const matches = loc.city?.toLowerCase().includes(search) || loc.country?.toLowerCase().includes(search);
      if (!matches) return false;
      const key = `${loc.city || ""}-${loc.country || ""}`;
      if (seen.has(key)) return false;
      seen.add(key); return true;
    });
    setSuggestions(filtered.slice(0, 6));
  }, [locationText, availableLocations]);

  const isDimmed = (key: ActiveSection) => active !== null && active !== key;
  const toggle   = (key: ActiveSection) => setActive((prev) => (prev === key ? null : key));

  const scrollCategories = (dir: "left" | "right") => {
    categoriesScrollRef.current?.scrollBy({ left: dir === "left" ? -240 : 240, behavior: "smooth" });
  };

  const handleTabClick = (path: string | ((id: string) => string)) => {
    if (!isAuthenticated || !user?.userId) { navigate("/AuthSwitch"); return; }
    navigate(typeof path === "function" ? path(user.userId) : path);
  };

  const applySearchFilters = () => {
    const payload: any = {};
    if (filters.address) payload.address = filters.address;
    if (checkIn && checkOut) {
      const days = dayjs(checkOut).diff(dayjs(checkIn), "day");
      if (days > 0) { payload.checkIn = dayjs(checkIn).format("YYYY-MM-DD"); payload.checkOut = dayjs(checkOut).format("YYYY-MM-DD"); payload.minDays = days; }
    }
    if (startHour !== null && endHour !== null) { payload.startHour = startHour; payload.endHour = endHour; }
    if (selectedBedroomId) payload.bedroomAmenityId = selectedBedroomId;
    if (selectedCategoryId) payload.category = selectedCategoryId;
    dispatch(setFilters(payload));
    setActive(null); setOpenCard(null);
  };

  const clearAllFilters = () => {
    dispatch(clearFilters());
    setLocationText(""); setSuggestions([]);
    setCheckIn(null); setCheckOut(null);
    setStartHour(null); setEndHour(null);
    setSelectedMonth(null); setFlexLength(null); setWhenMode("dates");
    setYears(1);
    setSelectedBedroomId(null);
    setSelectedAmenityIds([]);
    setCancellationFilter("any");
    setSelectedCategoryId(null); setOpenCard(null);
    setPriceRange({ min: 0, max: maxListingPrice });
  };

  const whenPreview = useMemo(() => {
    if (startHour !== null && endHour !== null)
      return { checkIn: `${String(startHour).padStart(2,"0")}:00`, checkOut: `${String(endHour).padStart(2,"0")}:00`, range: `${String(startHour).padStart(2,"0")}:00 → ${String(endHour).padStart(2,"0")}:00` };
    if (checkIn && checkOut)
      return { checkIn: dayjs(checkIn).format("MMM D YYYY"), checkOut: dayjs(checkOut).format("MMM D YYYY"), range: `${dayjs(checkIn).format("MMM D YYYY")} → ${dayjs(checkOut).format("MMM D YYYY")}` };
    return { checkIn: "Add dates", checkOut: "Add dates", range: "Any time" };
  }, [checkIn, checkOut, startHour, endHour]);

  const filteredListings = useMemo(() => {
    const toMidnightUTC = (d: string) => Date.parse(`${d.split("T")[0]}T00:00:00Z`);
    const selectedStart = checkIn  ? toMidnightUTC(dayjs(checkIn).format("YYYY-MM-DD"))  : null;
    const selectedEnd   = checkOut ? toMidnightUTC(dayjs(checkOut).format("YYYY-MM-DD")) : null;

    return listings.filter((item) => {
      const userManuallySearched = locationText.trim() !== "";
      const bounds  = filters.address?.bounds;
      const city    = filters.address?.city;
      const country = filters.address?.country;
      const iLat    = item.address?.latitude;
      const iLng    = item.address?.longitude;

      const locationMatch = (() => {
        if (!userManuallySearched && !country) return true;
        if (userManuallySearched && bounds && iLat && iLng)
          return iLat >= bounds.swLat && iLat <= bounds.neLat && iLng >= bounds.swLng && iLng <= bounds.neLng;
        if (userManuallySearched)
          return (city ? item.address?.city?.toLowerCase().includes(city.toLowerCase()) : true)
              && (country ? item.address?.country?.toLowerCase().includes(country.toLowerCase()) : true);
        if (country) return item.address?.country?.toLowerCase() === country.toLowerCase();
        return true;
      })();

      const rateTypeMatch = (() => {
        if (startHour !== null && endHour !== null) return item.rateType === "perHour";
        if (checkIn && checkOut) {
          const days   = dayjs(checkOut).diff(dayjs(checkIn), "day");
          const weeks  = dayjs(checkOut).diff(dayjs(checkIn), "week");
          const months = dayjs(checkOut).diff(dayjs(checkIn), "month");
          const yrs    = dayjs(checkOut).diff(dayjs(checkIn), "year");
          if (yrs >= 1)    return item.rateType === "perYear";
          if (months >= 1) return item.rateType === "perMonth";
          if (weeks >= 1)  return item.rateType === "perWeek";
          if (days >= 1)   return item.rateType === "perDay";
        }
        return true;
      })();

      const categoryMatch = filters.category ? item.category === filters.category : true;

      const bedroomMatch = selectedBedroomId
        ? Array.isArray(item.amenities) && item.amenities.includes(selectedBedroomId)
        : true;

      const amenityMatch = selectedAmenityIds.length > 0
        ? selectedAmenityIds.every(id => Array.isArray(item.amenities) && item.amenities.includes(id))
        : true;

      const cancellationMatch = (() => {
        if (cancellationFilter === "any") return true;
        const cp = item.cancellationPolicy;
        if (!cp) return cancellationFilter === "system";
        if (cancellationFilter === "free")    return cp.free    === true;
        if (cancellationFilter === "partial") return cp.partial === true;
        if (cancellationFilter === "system")  return cp.system  === true;
        return true;
      })();

      const isBlockedBySlice = selectedStart && selectedEnd ? blockedListingIds.includes(item.listingId) : false;
      const isBlockedByRes   = selectedStart && selectedEnd
        ? allReservations.some((r) => {
            if (!r.isConfirmed || r.isCancelled || r.isRefunded || r.listingId !== item.listingId) return false;
            const eS = toMidnightUTC(r.checkIn); const eE = toMidnightUTC(r.checkOut);
            return selectedStart < eE && selectedEnd > eS;
          }) : false;
      const isUnavailable = isBlockedBySlice || isBlockedByRes;

      const price      = convertRaw(item.rate ?? 0, "EUR");
      const priceMatch = price >= priceRange.min && price <= priceRange.max;

      return locationMatch && rateTypeMatch && categoryMatch && bedroomMatch && amenityMatch && cancellationMatch && priceMatch && !isUnavailable;
    });
  }, [listings, filters, allReservations, blockedListingIds, priceRange, checkIn, checkOut, startHour, endHour, convertRaw, locationText, selectedBedroomId, selectedAmenityIds, cancellationFilter]);

  /* ── Sub-components ── */
  const MobileExpandableCard = ({ id, title, preview, openCard, setOpenCard, children }: any) => {
    const open = openCard === id;
    return (
      <div style={S.mobileCard}>
        {!open ? (
          <div onClick={() => setOpenCard(id)} style={S.cardPreview}><span>{title}</span><strong>{preview}</strong></div>
        ) : (
          <><h3 style={{ padding: "16px 16px 0", color: GREEN }}>{title}</h3><div style={{ padding: 16 }}>{children}</div></>
        )}
      </div>
    );
  };

  const HourPicker = () => (
    <div style={{ padding: "22px 28px" }}>
      <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 18, color: GREEN }}>Select time slot (24h)</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div>
          <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 13 }}>Start hour</div>
          <input type="number" min={0} max={23} value={startHour ?? ""} placeholder="e.g. 9"
            onChange={(e) => setStartHour(Math.min(23, Math.max(0, Number(e.target.value))))}
            style={S.hourInput} />
          <div style={S.hourHint}>0 = midnight · 23 = 11 PM</div>
        </div>
        <div>
          <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 13 }}>End hour</div>
          <input type="number" min={1} max={24} value={endHour ?? ""} placeholder="e.g. 17"
            onChange={(e) => setEndHour(Math.min(24, Math.max(1, Number(e.target.value))))}
            style={S.hourInput} />
          <div style={S.hourHint}>Max 24 (midnight)</div>
        </div>
      </div>
      {startHour !== null && endHour !== null && endHour > startHour && (
        <div style={{ marginTop: 16, padding: "10px 16px", background: "#dcfce7", borderRadius: 12, fontWeight: 700, color: GREEN_DARK }}>
          <Clock size={14} style={{ marginRight: 6 }} />
          {endHour - startHour}h slot · {String(startHour).padStart(2,"0")}:00 → {String(endHour).padStart(2,"0")}:00
        </div>
      )}
      <div style={{ marginTop: 20 }}>
        <div style={{ fontWeight: 600, fontSize: 12, color: "#888", marginBottom: 10 }}>QUICK SELECT</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {[{ label: "Morning 6–12", s: 6, e: 12 }, { label: "Afternoon 12–18", s: 12, e: 18 },
            { label: "Evening 18–22", s: 18, e: 22 }, { label: "Full Day 8–18", s: 8, e: 18 },
            { label: "Half Day 8–13", s: 8, e: 13 }].map((q) => (
            <button key={q.label} onClick={() => { setStartHour(q.s); setEndHour(q.e); }}
              style={{ ...S.quickBtn, background: startHour === q.s && endHour === q.e ? GREEN_GRADIENT : "#f0fdf4",
                       color: startHour === q.s && endHour === q.e ? "#fff" : GREEN_DARK }}>
              {q.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const YearPicker = () => (
    <div style={{ padding: "32px 28px", textAlign: "center" }}>
      <h3 style={{ marginBottom: 8, color: GREEN_DARK }}>How many years?</h3>
      <p style={{ fontSize: 12, color: "#888", marginBottom: 28 }}>Starting from today</p>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 28 }}>
        <button onClick={() => setYears(y => Math.max(1, y - 1))}
          style={{ width: 44, height: 44, borderRadius: "50%", border: `1.5px solid ${GREEN}`, background: "#fff", color: GREEN, cursor: "pointer", display: "grid", placeItems: "center" }}>
          <Minus size={18} />
        </button>
        <div style={{ minWidth: 80 }}>
          <div style={{ fontSize: 56, fontWeight: 800, color: GREEN_DARK, lineHeight: 1 }}>{years}</div>
          <div style={{ fontSize: 13, color: "#888", marginTop: 4 }}>{years === 1 ? "year" : "years"}</div>
        </div>
        <button onClick={() => setYears(y => Math.min(10, y + 1))}
          style={{ width: 44, height: 44, borderRadius: "50%", border: "none", background: GREEN_GRADIENT, color: "#fff", cursor: "pointer", display: "grid", placeItems: "center" }}>
          <Plus size={18} />
        </button>
      </div>
      <div style={{ marginTop: 24, padding: "12px 20px", background: "#dcfce7", borderRadius: 12, fontSize: 13, fontWeight: 700, color: GREEN_DARK }}>
        {dayjs().format("MMM D YYYY")} → {dayjs().add(years, "year").format("MMM D YYYY")}
      </div>
    </div>
  );

  const MonthPicker = ({ mobile }: { mobile?: boolean }) => (
    <div style={mobile ? S.monthGrid : { display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, padding: "32px 28px" }}>
      {!mobile && <h3 style={{ gridColumn: "1/-1", marginBottom: 4, color: GREEN_DARK }}>When do you need cleaning?</h3>}
      {Array.from({ length: 12 }).map((_, i) => {
        const m = new Date(); m.setMonth(m.getMonth() + i);
        const sel = selectedMonth === i;
        return (
          <button key={i} onClick={() => {
            setSelectedMonth(i);
            const start = i === 0 ? dayjs() : dayjs().add(i, "month").startOf("month");
            setCheckIn(start.toDate()); setCheckOut(start.add(1, "month").toDate());
          }} style={{ ...S.monthBtn, background: sel ? GREEN_GRADIENT : "#fff", color: sel ? "#fff" : "#333" }}>
            {m.toLocaleString(undefined, { month: "short" })}
          </button>
        );
      })}
    </div>
  );

  const WhenTabs = ({ mobile }: { mobile?: boolean }) => (
    <div style={mobile ? S.whenToggle : { display: "flex", gap: 10 }}>
      {(["dates","hours","months","flex","years"] as WhenMode[]).map((key) => (
        <button key={key} onClick={() => setWhenMode(key)}
          style={mobile
            ? { ...S.whenToggleBtn, background: whenMode === key ? GREEN_GRADIENT : "transparent", color: whenMode === key ? "#fff" : "#555" }
            : { padding: "6px 14px", borderRadius: 9999, fontWeight: 700, fontSize: 14, border: "none", cursor: "pointer",
                background: whenMode === key ? GREEN_GRADIENT : "transparent", color: whenMode === key ? "#fff" : "#666" }}>
          {key === "dates" ? "Dates" : key === "hours" ? "Hours" : key === "months" ? "Months" : key === "flex" ? (mobile ? "Flex" : "Flexible") : "Years"}
        </button>
      ))}
    </div>
  );

  const DesktopCalendar = () => (
    <div style={{ padding: "22px 28px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <button onClick={() => setMonthOffset((o) => Math.max(0, o - 1))} disabled={monthOffset === 0}
          style={{ ...S.navBtn, opacity: monthOffset === 0 ? 0.3 : 1 }}><ChevronLeft size={16} /></button>
        <button onClick={() => setMonthOffset((o) => o + 1)} style={S.navBtn}><ChevronRight size={16} /></button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32 }}>
        {[0, 1].map((m) => {
          const offset = monthOffset + m;
          const today  = new Date(); today.setHours(0,0,0,0);
          const base   = new Date(); base.setMonth(base.getMonth() + offset);
          const monthName   = base.toLocaleString(undefined, { month: "long", year: "numeric" });
          const daysInMonth = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
          const firstDay    = new Date(base.getFullYear(), base.getMonth(), 1).getDay();
          const grid = [...Array(firstDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
          return (
            <div key={offset}>
              <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 16 }}>{monthName}</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", textAlign: "center", fontSize: 13, color: "#666", marginBottom: 6 }}>
                {["S","M","T","W","T","F","S"].map((d, i) => <div key={i}>{d}</div>)}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 6 }}>
                {grid.map((day, i) => {
                  if (day === null) return <div key={i} />;
                  const date = new Date(base.getFullYear(), base.getMonth(), day);
                  const past     = date < today;
                  const selected = (checkIn && date.toDateString() === checkIn.toDateString()) || (checkOut && date.toDateString() === checkOut.toDateString());
                  const inRange  = checkIn && checkOut && date > checkIn && date < checkOut;
                  return (
                    <div key={i} style={{ position: "relative" }}>
                      {inRange && <div style={{ position: "absolute", inset: 0, background: "rgba(22,163,74,0.15)", borderRadius: 8 }} />}
                      <button disabled={past}
                        onClick={() => {
                          if (!checkIn || (checkIn && checkOut)) { setCheckIn(date); setCheckOut(null); }
                          else { if (date < checkIn) { setCheckOut(checkIn); setCheckIn(date); } else setCheckOut(date); }
                        }}
                        style={{ width: "100%", height: 38, borderRadius: 10, border: "none", fontWeight: 600,
                          cursor: past ? "not-allowed" : "pointer",
                          background: selected ? GREEN_GRADIENT : "transparent",
                          color: past ? "#ccc" : selected ? "#fff" : "#333" }}>
                        {day}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const MobileCalendar = () => {
    const today   = new Date(); today.setHours(0,0,0,0);
    const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
    const inRange = (d: Date) => checkIn && checkOut && d > checkIn && d < checkOut;
    const handleSelect = (date: Date) => {
      if (!checkIn || (checkIn && checkOut)) { setCheckIn(date); setCheckOut(null); return; }
      if (date < checkIn) { setCheckIn(date); return; }
      setCheckOut(date);
    };
    return (
      <div style={{ maxHeight: 500, overflowY: "auto" }}>
        {Array.from({ length: 24 }, (_, offset) => {
          const base = new Date(); base.setMonth(base.getMonth() + offset);
          const year = base.getFullYear(), month = base.getMonth();
          const daysInMonth = new Date(year, month + 1, 0).getDate();
          const firstDay    = new Date(year, month, 1).getDay();
          const days: (Date | null)[] = [...Array(firstDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1))];
          return (
            <div key={offset} style={{ marginBottom: 28 }}>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12, textAlign: "center" }}>
                {base.toLocaleString(undefined, { month: "long", year: "numeric" })}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", textAlign: "center", fontSize: 12, color: "#666", marginBottom: 6 }}>
                {["S","M","T","W","T","F","S"].map((d, i) => <div key={i} style={{ fontWeight: 600 }}>{d}</div>)}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2 }}>
                {days.map((day, i) => {
                  if (!day) return <div key={i} />;
                  const disabled = day < today;
                  const start = checkIn && sameDay(day, checkIn);
                  const end   = checkOut && sameDay(day, checkOut);
                  return (
                    <div key={i} style={{ position: "relative", display: "flex", justifyContent: "center" }}>
                      {inRange(day) && <div style={{ position: "absolute", inset: 0, background: "rgba(22,163,74,0.15)" }} />}
                      <button disabled={disabled} onClick={() => handleSelect(day)}
                        style={{ position: "relative", zIndex: 1, width: 36, height: 36, borderRadius: "50%", border: "none", fontWeight: 600,
                          background: start || end ? GREEN_GRADIENT : "transparent",
                          color: disabled ? "#ccc" : start || end ? "#fff" : "#333",
                          cursor: disabled ? "not-allowed" : "pointer" }}>
                        {day.getDate()}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const BedroomPicker = () => (
    <div style={{ padding: 16 }}>
      <div style={{ fontWeight: 700, fontSize: 13, color: GREEN, marginBottom: 12 }}>Property Size</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {cleaningAmenities.filter(a => BEDROOM_AMENITY_IDS.includes(a.id)).map((a) => {
          const sel = selectedBedroomId === a.id;
          return (
            <div key={a.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #eee" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ color: sel ? GREEN : "#777", fontSize: 20 }}>{a.icon}</span>
                <span style={{ fontWeight: sel ? 700 : 500, color: sel ? GREEN_DARK : "#333", fontSize: 13 }}>{a.name}</span>
              </div>
              <button onClick={() => setSelectedBedroomId(sel ? null : a.id)}
                style={{ width: 28, height: 28, borderRadius: "50%", border: `1.5px solid ${sel ? GREEN : "#ddd"}`, background: sel ? GREEN : "#fff", color: sel ? "#fff" : "#555", display: "grid", placeItems: "center", cursor: "pointer", fontWeight: 700, fontSize: 14 }}>
                {sel ? "✓" : "+"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div style={S.wrapper}>

      {/* ========== HEADER ========== */}
      <section style={S.searchWrapper}>
        <div style={S.centerWrap}>

          {/* ── DESKTOP ── */}
          {!isMobile && (
            <div style={S.searchAnchor}>
              <div style={S.searchPill}>

                <SearchItem title="Where" subtitle={locationPreview} active={active === "where"} dimmed={isDimmed("where")} onClick={() => toggle("where")} />
                <Divider />

                <div onClick={() => toggle("dates")} style={{ display: "flex", borderRadius: 999,
                  background: active === "dates" ? "#fff" : isDimmed("dates") ? GREY_BG : "transparent",
                  boxShadow: active === "dates" ? "0 6px 16px rgba(0,0,0,.18)" : "none",
                  opacity: isDimmed("dates") ? 0.6 : 1, cursor: "pointer" }}>
                  <div style={{ ...S.searchItem, boxShadow: "none" }}>
                    <div style={S.searchTitle}>Check in</div>
                    <div style={S.searchSubtitle}>{whenPreview.checkIn}</div>
                  </div>
                  <Divider />
                  <div style={{ ...S.searchItem, boxShadow: "none" }}>
                    <div style={S.searchTitle}>Check out</div>
                    <div style={S.searchSubtitle}>{whenPreview.checkOut}</div>
                  </div>
                </div>
                <Divider />

                <div onClick={() => toggle("who")} style={{ display: "flex", alignItems: "center", borderRadius: 999,
                  background: active === "who" ? "#fff" : isDimmed("who") ? GREY_BG : "transparent",
                  boxShadow: active === "who" ? "0 6px 16px rgba(0,0,0,.18)" : "none",
                  opacity: isDimmed("who") ? 0.6 : 1, cursor: "pointer" }}>
                  <div style={{ ...S.searchItem, boxShadow: "none" }}>
                    <div style={S.searchTitle}>Property Size</div>
                    <div style={S.searchSubtitle}>{bedroomPreview}</div>
                  </div>
                  <button style={{ ...S.searchBtn, marginRight: 8 }} onClick={(e) => { e.stopPropagation(); applySearchFilters(); }}>
                    <Search size={18} color="#fff" />
                  </button>
                </div>

                {/* DATES MODAL */}
                {active === "dates" && (
                  <AnchorModal full>
                    <div style={{ padding: "18px 28px", borderBottom: "1px solid rgba(0,0,0,0.08)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <WhenTabs />
                      <div style={{ fontWeight: 700, fontSize: 15, color: "#555" }}>{whenPreview.range}</div>
                    </div>
                    {whenMode === "dates"  && <DesktopCalendar />}
                    {whenMode === "hours"  && <HourPicker />}
                    {whenMode === "months" && <MonthPicker />}
                    {whenMode === "years"  && <YearPicker />}
                    {whenMode === "flex"   && (
                      <div style={{ padding: "32px 28px" }}>
                        <h3 style={{ marginBottom: 20 }}>How long do you need?</h3>
                        <div style={{ display: "flex", gap: 12 }}>
                          {(["4h","8h","Full Day"] as const).map((label) => (
                            <button key={label} onClick={() => {
                              setFlexLength(label);
                              const h = label === "4h" ? 4 : label === "8h" ? 8 : 10;
                              setStartHour(8); setEndHour(8 + h);
                            }} style={{ padding: "14px 22px", borderRadius: 999, border: "1px solid #e5e5e5", fontWeight: 700, cursor: "pointer",
                              background: flexLength === label ? GREEN_GRADIENT : "#fff", color: flexLength === label ? "#fff" : "#333" }}>
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </AnchorModal>
                )}

                {/* WHERE MODAL */}
                {active === "where" && (
                  <AnchorModal small left>
                    <div style={{ width: 320 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                        <div style={{ fontWeight: 700, fontSize: 16, color: GREEN }}>Where</div>
                        <button onClick={clearAllFilters} style={S.clearTextBtn}>Clear</button>
                      </div>
                      <TextField fullWidth inputRef={inputRef} value={locationText} onChange={(e) => setLocationText(e.target.value)}
                        label="Search destinations" variant="outlined"
                        InputLabelProps={{ style: { fontWeight: 600 } }}
                        inputProps={{ style: { padding: "14px", fontSize: 14 } }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "14px", backgroundColor: "#fff",
                          "& fieldset": { borderColor: GREEN }, "&:hover fieldset": { borderColor: GREEN_DARK } } }}
                      />
                      {suggestions.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          {suggestions.map((loc, idx) => {
                            const label = [loc.neighborhood, loc.city, loc.country].filter(Boolean).join(", ");
                            return (
                              <div key={idx} onClick={() => {
                                setLocationText(label);
                                dispatch(setFilters({ address: { neighborhood: loc.neighborhood || null, city: loc.city || null, country: loc.country || null, latitude: null, longitude: null, bounds: null } }));
                                setSuggestions([]); setActive("dates"); setOpenCard("when");
                              }} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 12, cursor: "pointer" }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = "#f0fdf4")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                                <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#dcfce7", display: "grid", placeItems: "center" }}>
                                  <MapPin size={18} color={GREEN} />
                                </div>
                                <div>
                                  <div style={{ fontWeight: 700, fontSize: 14 }}>{[loc.neighborhood, loc.city].filter(Boolean).join(", ")}</div>
                                  <div style={{ fontSize: 12, color: "#777" }}>{loc.country}</div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </AnchorModal>
                )}

                {/* PROPERTY SIZE MODAL */}
                {active === "who" && (
                  <AnchorModal small right>
                    <div style={{ width: 320 }}>
                      <BedroomPicker />
                    </div>
                  </AnchorModal>
                )}

              </div>
            </div>
          )}

          {/* ── MOBILE ── */}
          {isMobile && (
            <div style={{ position: "relative", width: "100%", display: "flex", alignItems: "center", gap: 10 }}>
              <div onClick={() => toggle("mobile")} style={{ ...S.mobilePill, flex: 1, cursor: "pointer" }}>
                <div style={S.mobileBlock}>
                  <div style={S.searchTitle}>Where</div>
                  <div style={S.searchSubtitle}>{locationPreview}</div>
                </div>
                <Divider />
                <div style={S.mobileBlock}>
                  <div style={S.searchTitle}>Size</div>
                  <div style={S.searchSubtitle}>{bedroomPreview}</div>
                </div>
                <button style={{ ...S.searchBtn, marginRight: 8 }} onClick={(e) => { e.stopPropagation(); toggle("mobile"); }}>
                  <Search size={18} color="#fff" />
                </button>
              </div>

              {isAuthenticated && (
                <button style={S.notificationBtn} onClick={() => navigate(`/notifications/${user?.userId}`)}>
                  <Bell size={18} />
                  {unreadCount > 0 && <span style={S.notificationBadge}>{unreadCount > 99 ? "99+" : unreadCount}</span>}
                </button>
              )}

              {active === "mobile" && (
                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50, display: "flex", justifyContent: "center", pointerEvents: "none" }}>
                  <div style={{ ...S.mobileAnchor, marginTop: 8, pointerEvents: "auto" }}>
                    <div style={S.mobileSheetBackdrop}>
                      <div style={S.mobileSheet}>
                        <div style={S.sheetHeader}>
                          <div style={{ fontWeight: 700, color: GREEN }}>Search Cleaners</div>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                            <button onClick={() => setActive(null)} style={S.closeGreenBtn}>Close</button>
                            <button onClick={clearAllFilters} style={S.clearTextBtn}>Clear</button>
                          </div>
                        </div>

                        <MobileExpandableCard id="where" title="Where" preview={locationPreview || "I'm flexible"} openCard={openCard} setOpenCard={setOpenCard}>
                          <TextField fullWidth inputRef={inputRef} value={locationText} onChange={(e) => setLocationText(e.target.value)} placeholder="Search destinations" variant="outlined" />
                          {suggestions.length > 0 && (
                            <div style={{ marginTop: 12 }}>
                              {suggestions.map((loc, idx) => (
                                <div key={idx} onClick={() => {
                                  setLocationText([loc.neighborhood, loc.city, loc.country].filter(Boolean).join(", "));
                                  dispatch(setFilters({ address: { neighborhood: loc.neighborhood || null, city: loc.city || null, country: loc.country || null, latitude: null, longitude: null, bounds: null } }));
                                  setSuggestions([]); setOpenCard("when");
                                }} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 12, cursor: "pointer" }}>
                                  <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#dcfce7", display: "grid", placeItems: "center", flexShrink: 0 }}>
                                    <MapPin size={18} color={GREEN} />
                                  </div>
                                  <div>
                                    <div style={{ fontWeight: 700, fontSize: 14 }}>{loc.city || loc.neighborhood}</div>
                                    <div style={{ fontSize: 12, color: "#777" }}>{loc.country}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </MobileExpandableCard>

                        <MobileExpandableCard id="when" title="When" preview={whenPreview.range} openCard={openCard} setOpenCard={setOpenCard}>
                          <WhenTabs mobile />
                          {whenMode === "dates"  && <MobileCalendar />}
                          {whenMode === "hours"  && <HourPicker />}
                          {whenMode === "months" && <MonthPicker mobile />}
                          {whenMode === "years"  && <YearPicker />}
                          {whenMode === "flex"   && (
                            <div style={{ display: "flex", gap: 12 }}>
                              {(["4h","8h","Full Day"] as const).map((f) => (
                                <button key={f} onClick={() => {
                                  setFlexLength(f);
                                  const h = f === "4h" ? 4 : f === "8h" ? 8 : 10;
                                  setStartHour(8); setEndHour(8 + h);
                                }} style={{ ...S.flexBtn, background: flexLength === f ? GREEN_GRADIENT : "#fff", color: flexLength === f ? "#fff" : "#333" }}>
                                  {f}
                                </button>
                              ))}
                            </div>
                          )}
                        </MobileExpandableCard>

                        <MobileExpandableCard id="who" title="Property Size" preview={bedroomPreview} openCard={openCard} setOpenCard={setOpenCard}>
                          <BedroomPicker />
                        </MobileExpandableCard>

                        <MobileExpandableCard id="search" title="Search" preview="Show cleaners" openCard={openCard} setOpenCard={setOpenCard}>
                          <button style={S.mobileSearchBtn} onClick={applySearchFilters}>Search</button>
                        </MobileExpandableCard>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* DESKTOP RIGHT */}
        {!isMobile && (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {isAuthenticated && (
              <button style={S.notificationBtn} onClick={() => navigate(`/notifications/${user?.userId}`)}>
                <Bell size={18} />
                {unreadCount > 0 && <span style={S.notificationBadge}>{unreadCount > 99 ? "99+" : unreadCount}</span>}
              </button>
            )}
            <button style={S.profileMenuBtn} onClick={() => { if (!isAuthenticated) navigate("/AuthSwitch"); else toggle("menu"); }}>
              {isAuthenticated ? (
                user?.profilePicture ? (
                  <img src={user.profilePicture} alt={user.firstName} style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }} />
                ) : (
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: GREEN_GRADIENT, color: "#fff", display: "grid", placeItems: "center", fontWeight: 700, fontSize: 14 }}>
                    {user?.firstName?.charAt(0).toUpperCase()}
                  </div>
                )
              ) : <User size={18} />}
              <Menu size={18} />
            </button>
          </div>
        )}
      </section>

      <div style={S.separator} />

      {/* MOBILE SUBHEADER */}
      {isMobile && (
        <section style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", overflow: "hidden" }}>
          <button style={S.filtersBtn} onClick={() => toggle("filters")}><Tune fontSize="small" /></button>
          <div style={{ flex: 1, overflowX: "auto", display: "flex", gap: 14, scrollbarWidth: "none" }}>
            {cleaningCategories.map((cat) => {
              const sel = selectedCategoryId === cat.id;
              return (
                <button key={cat.id} onClick={() => { const n = sel ? null : cat.id; setSelectedCategoryId(n); dispatch(setFilters({ ...filters, category: n })); }}
                  style={{ minWidth: 72, padding: "6px 4px", border: "none", background: "transparent", display: "flex", flexDirection: "column", alignItems: "center", opacity: sel ? 1 : 0.65 }}>
                  <div style={{ fontSize: 18, marginBottom: 4, color: sel ? GREEN : "#777" }}>{cat.icon}</div>
                  <div style={{ fontSize: 11, fontWeight: sel ? 700 : 600, whiteSpace: "nowrap", background: sel ? LUXURY_GREEN : "none",
                    WebkitBackgroundClip: sel ? "text" : undefined, WebkitTextFillColor: sel ? "transparent" : undefined, color: sel ? undefined : "#555" }}>
                    {cat.name}
                  </div>
                  <div style={{ marginTop: 4, height: 2, width: "100%", borderRadius: 2, background: sel ? GREEN_GRADIENT : "transparent" }} />
                </button>
              );
            })}
          </div>
          <TaxToggle taxOn={taxOn} setTaxOn={setTaxOn} />
        </section>
      )}

      <div style={S.separator} />

      {/* DESKTOP CATEGORIES */}
      {!isMobile && (
        <>
          <div style={S.separator} />
          <section style={S.categoriesRow}>
            <div style={S.categoriesWrap}>
              <button style={S.navBtn} onClick={() => scrollCategories("left")}><ChevronLeft size={16} /></button>
              <div ref={categoriesScrollRef} style={S.categoriesScroll}>
                {cleaningCategories.map((cat) => {
                  const sel = selectedCategoryId === cat.id;
                  return (
                    <button key={cat.id} onClick={() => { const n = sel ? null : cat.id; setSelectedCategoryId(n); dispatch(setFilters({ ...filters, category: n })); }}
                      style={{ ...S.categoryItem, opacity: sel ? 1 : 0.6 }}>
                      <div style={{ fontSize: 18, marginBottom: 4, color: sel ? GREEN : "#777" }}>{cat.icon}</div>
                      <div style={{ fontWeight: sel ? 700 : 600, background: sel ? LUXURY_GREEN : "none",
                        WebkitBackgroundClip: sel ? "text" : undefined, WebkitTextFillColor: sel ? "transparent" : undefined, color: sel ? undefined : "#555" }}>
                        {cat.name}
                      </div>
                      <div style={{ marginTop: 6, height: 2, width: "100%", borderRadius: 2, background: sel ? GREEN_GRADIENT : "transparent", transition: "background .25s ease" }} />
                    </button>
                  );
                })}
              </div>
              <button style={S.navBtn} onClick={() => scrollCategories("right")}><ChevronRight size={16} /></button>
            </div>
            <div style={S.rightControls}>
              <button style={S.filtersBtn} onClick={() => toggle("filters")}><Tune fontSize="small" /> Filters</button>
              <TaxToggle taxOn={taxOn} setTaxOn={setTaxOn} />
            </div>
          </section>
          <div style={S.separator} />
        </>
      )}

      {/* LISTINGS GRID */}
      <div style={S.listingsGrid}>
        {filteredListings.length > 0 ? (
          filteredListings.map((item) => (
            <CleaningCard key={item.listingId} item={item} userId={user?.userId} wishlist={wishlist}
              checkIn={checkIn ? dayjs(checkIn).format("YYYY-MM-DD") : null}
              checkOut={checkOut ? dayjs(checkOut).format("YYYY-MM-DD") : null}
              startHour={startHour} endHour={endHour}
              bedroomAmenityId={selectedBedroomId}
              selectedAmenityIds={selectedAmenityIds} />
          ))
        ) : (
          <div style={{ display: "flex", justifyContent: "center", padding: "60px 20px" }}>
            <div style={{ background: GREEN_GRADIENT, color: "#fff", padding: "22px 32px", borderRadius: 6, minWidth: 280, maxWidth: 420, textAlign: "center", fontWeight: 700, fontSize: 16, boxShadow: "0 10px 30px rgba(22,163,74,0.45)" }}>
              <div>No cleaners found</div>
              <button onClick={clearAllFilters} style={{ marginTop: 12, background: "transparent", border: "none", color: "#fff", fontSize: 14, fontWeight: 600, textDecoration: "underline", cursor: "pointer" }}>
                View all cleaners
              </button>
            </div>
          </div>
        )}
      </div>

      {/* FILTER MODAL */}
      {active === "filters" && (
        <div onClick={() => setActive(null)} style={{ position: "fixed", inset: 0, zIndex: 20000, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)", display: "flex", justifyContent: "center", alignItems: isMobile ? "flex-end" : "center", padding: isMobile ? 0 : 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: isMobile ? "100%" : 560, maxWidth: "92vw", background: "#fff", borderRadius: isMobile ? "24px 24px 0 0" : 22, padding: isMobile ? "22px 20px 100px" : 28, boxShadow: "0 30px 70px rgba(0,0,0,.35)", maxHeight: "85vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <div style={{ fontWeight: 800, fontSize: 18, color: GREEN }}>Filters</div>
              <button onClick={() => setActive(null)} style={{ border: "none", background: "#f3f3f3", width: 34, height: 34, borderRadius: "50%", cursor: "pointer" }}><X size={16} /></button>
            </div>

            {/* Price */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontWeight: 700, marginBottom: 12 }}>Price range</div>
              <div style={{ fontWeight: 600, marginBottom: 14 }}>{symbol}{priceRange.min.toLocaleString()} — {symbol}{priceRange.max.toLocaleString()}</div>
              <input type="range" min={0} max={maxListingPrice} value={priceRange.max} onChange={(e) => setPriceRange({ ...priceRange, max: Number(e.target.value) })} style={{ width: "100%", accentColor: GREEN }} />
            </div>

            {/* Amenities */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontWeight: 700, marginBottom: 12 }}>Included Services</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {cleaningAmenities.map((a) => {
                  const on = selectedAmenityIds.includes(a.id);
                  return (
                    <button key={a.id} onClick={() => setSelectedAmenityIds(prev => on ? prev.filter(x => x !== a.id) : [...prev, a.id])}
                      style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 13px", borderRadius: 999, border: `1.5px solid ${on ? GREEN : "#ddd"}`, background: on ? "#dcfce7" : "#fff", color: on ? GREEN_DARK : "#555", fontWeight: on ? 700 : 500, fontSize: 12, cursor: "pointer" }}>
                      <span style={{ fontSize: 14 }}>{a.icon}</span>
                      {a.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Cancellation */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontWeight: 700, marginBottom: 12 }}>Cancellation Policy</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {[
                  { key: "any",     label: "Any policy",        desc: "Show all listings" },
                  { key: "free",    label: "Free cancellation", desc: "Full refund if cancelled" },
                  { key: "partial", label: "Partial (50%)",     desc: "Each party keeps 50%" },
                  { key: "system",  label: "System default",    desc: "Standard platform policy" },
                ].map(({ key, label, desc }) => {
                  const on = cancellationFilter === key;
                  return (
                    <div key={key} onClick={() => setCancellationFilter(key as any)}
                      style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderRadius: 12, border: `1.5px solid ${on ? GREEN : "#eee"}`, background: on ? "#f0fdf4" : "#fff", cursor: "pointer" }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13, color: on ? GREEN_DARK : "#333" }}>{label}</div>
                        <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{desc}</div>
                      </div>
                      <div style={{ width: 20, height: 20, borderRadius: "50%", border: `2px solid ${on ? GREEN : "#ddd"}`, background: on ? GREEN : "#fff", display: "grid", placeItems: "center" }}>
                        {on && <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#fff" }} />}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ marginTop: 30, display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button onClick={() => { setPriceRange({ min: 0, max: maxListingPrice }); setSelectedAmenityIds([]); setCancellationFilter("any"); }}
                style={{ padding: "12px 18px", borderRadius: 12, border: "1px solid #ddd", background: "#fff", fontWeight: 700, cursor: "pointer" }}>Clear</button>
              <button onClick={() => setActive(null)}
                style={{ padding: "12px 22px", background: GREEN_GRADIENT, color: "#fff", border: "none", borderRadius: 12, fontWeight: 800, cursor: "pointer", boxShadow: "0 8px 20px rgba(22,163,74,.4)" }}>Apply</button>
            </div>
          </div>
        </div>
      )}

      {/* SIDE MENU */}
      {active === "menu" && (
        <div style={S.sidebarOverlay} onClick={() => setActive(null)}>
          <div style={S.sidebar} onClick={(e) => e.stopPropagation()}>
            <button style={S.closeBtn} onClick={() => setActive(null)}><X size={18} /></button>
            <div style={S.sidebarContent}>
              {isAuthenticated ? (
                <>
                  <button onClick={() => navigate(`/cleaningdashboard/${user?.userId}`)} style={S.sidebarItem}>Dashboard</button>
                  <button onClick={() => navigate(`/CleaningBookingsDashboard/${user?.userId}`)} style={S.sidebarItem}>Reservations</button>
                  <button onClick={() => navigate(`/wishlist/${user?.userId}`)} style={S.sidebarItem}>Wishlists</button>
                  <button onClick={() => navigate(`/notifications/${user?.userId}`)} style={S.sidebarItem}>Notifications</button>
                  <button onClick={() => navigate("/imessagepreview/")} style={S.sidebarItem}>Messages</button>
                  <button onClick={() => navigate(`/profile/${user?.userId}`)} style={S.sidebarItem}>Profile</button>
                  <button onClick={() => navigate(`/profileSettings/${user?.userId}`)} style={S.sidebarItem}>Account Settings</button>
                  <button onClick={() => navigate("/CurrencySelection")} style={S.sidebarItem}>Language & Currency</button>
                  <button onClick={() => navigate("/CustomerSupport")} style={S.sidebarItem}>Help</button>
                  <button onClick={() => dispatch({ type: "auth/logout" })} style={S.sidebarItem}>Logout</button>
                </>
              ) : (
                <>
                  <button onClick={() => navigate("/AuthSwitch")} style={S.sidebarItem}>Login</button>
                  <button onClick={() => navigate("/AuthSwitch")} style={S.sidebarItem}>Register</button>
                  <button onClick={() => navigate("/CurrencySelection")} style={S.sidebarItem}>Language & Currency</button>
                  <button onClick={() => navigate("/CustomerSupport")} style={S.sidebarItem}>Help Center</button>
                  <button onClick={() => navigate("/AuthSwitch")} style={S.sidebarItem}>Become a Cleaner</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MOBILE BOTTOM TABS */}
      {isMobile && (
        <nav style={S.mobileTabs}>
          <BottomTab icon={<Home size={22} />} label="Home" active onClick={() => handleTabClick("/")} />
          <BottomTab icon={<Compass size={22} />} label="Explore" onClick={() => handleTabClick("/explore")} />
          <BottomTab icon={<Calendar size={22} />} label="Bookings" onClick={() => handleTabClick((id) => `/CleaningBookingsDashboard/${user?.userId}`)} />
          <BottomTab icon={<MessageCircle size={22} />} label="Chat" onClick={() => handleTabClick(() => "/imessagepreview/")} />
          <BottomTab label={user?.firstName || "Profile"} avatarUrl={user?.profilePicture} icon={<User size={22} />} onClick={() => handleTabClick((id) => `/profile/${id}`)} />
        </nav>
      )}
    </div>
  );
};

export default CleaningHome;

/* ======================================================
   CLEANING CARD
====================================================== */
interface CleaningCardProps {
  item: { listingId: string; photos?: string[]; rate?: number; rateType?: string; address?: { city?: string; country?: string }; amenities?: number[]; cancellationPolicy?: any; [key: string]: any };
  userId?: string;
  wishlist: { listingId: string }[];
  checkIn: string | null;
  checkOut: string | null;
  startHour: number | null;
  endHour: number | null;
  bedroomAmenityId: number | null;
  selectedAmenityIds: number[];
}

const CleaningCard: React.FC<CleaningCardProps> = ({ item, userId, wishlist, checkIn, checkOut, startHour, endHour, bedroomAmenityId, selectedAmenityIds }) => {
  const dispatch   = useDispatch<AppDispatch>();
  const navigate   = useNavigate();
  const { convertRaw, symbol } = useCurrency();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [avgRating, setAvgRating]       = useState<number | null>(null);
  const [totalReviews, setTotalReviews] = useState(0);

  const summary    = useSelector((s: RootState) => s.cleaningRatings.summaries?.[item.listingId]);
  const finalPrice = useSelector((s: RootState) => s.cleaningServiceFees.finalPricesByListing?.[item.listingId]);
  const isWishlisted = wishlist.some((w) => w.listingId === item.listingId);

  useEffect(() => {
    if (summary && summary.totalReviews >= 5) { setAvgRating(summary.avgRating ?? 0); setTotalReviews(summary.totalReviews); }
    else { setAvgRating(0); setTotalReviews(0); }
  }, [summary]);

  const nextSlide = (e: React.MouseEvent) => { e.stopPropagation(); setCurrentIndex((p) => (p + 1) % (item.photos?.length || 1)); };
  const prevSlide = (e: React.MouseEvent) => { e.stopPropagation(); setCurrentIndex((p) => (p - 1 + (item.photos?.length || 1)) % (item.photos?.length || 1)); };

  const toggleWishlist = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!userId) return alert("Please log in first");
    const type = "cleaning";
    if (isWishlisted) dispatch(removeFromWishlist({ userId, listingId: item.listingId, type }));
    else dispatch(addToWishlist({ userId, listingId: item.listingId, type }));
  };

  const handleClick = () => {
    const q = new URLSearchParams();
    if (checkIn)  q.set("checkIn", checkIn);
    if (checkOut) q.set("checkOut", checkOut);
    if (startHour !== null) q.set("startHour", String(startHour));
    if (endHour !== null)   q.set("endHour",   String(endHour));
    if (bedroomAmenityId)   q.set("bedroomAmenityId", String(bedroomAmenityId));
    if (selectedAmenityIds.length > 0) q.set("amenityIds", selectedAmenityIds.join(","));
    navigate(`/cleaningdetails/${item.listingId}${q.toString() ? `?${q.toString()}` : ""}`);
  };

  const rateLabel = item.rateType === "perHour" ? "hour" : item.rateType === "perDay" ? "day" : item.rateType === "perWeek" ? "week" : item.rateType === "perMonth" ? "month" : "year";

  return (
    <div style={CS.card} onClick={handleClick}>
      <div style={CS.imageWrapper}>
        {item.photos && item.photos.length > 1 && <button style={CS.chevronLeft} onClick={prevSlide}><ChevronLeft size={20} /></button>}
        <img src={item.photos?.[currentIndex] || "https://via.placeholder.com/300"} alt="Cleaner" style={CS.image} />
        {item.photos && item.photos.length > 1 && <button style={CS.chevronRight} onClick={nextSlide}><ChevronRight size={20} /></button>}
      </div>

      <button style={CS.heartButton} onClick={toggleWishlist}>
        {isWishlisted ? <FilledHeartIcon color="#16a34a" size={35} /> : <OutlinedHeartIcon color="#fff" size={35} />}
      </button>

      <div style={CS.locationDome}>
        <MapPin size={14} style={{ marginRight: 6 }} />
        {[item.address?.city, item.address?.country].filter(Boolean).join(", ")}
      </div>

      {totalReviews >= 5 ? (
        <div style={CS.ratingDome}><Star size={14} /> {avgRating?.toFixed(1)} ({totalReviews})</div>
      ) : (
        <div style={CS.noRatingDome}>No reviews</div>
      )}

      {finalPrice && (
        <div style={CS.finalPriceDome}>
          <span style={{ opacity: 0.85, fontWeight: 600, marginRight: 6 }}>You Pay:</span>
          {symbol}{Number(convertRaw(finalPrice.totalCharged ?? 0, "EUR")).toLocaleString()}
        </div>
      )}

      <div style={CS.priceDome}>
        {item.rate ? <>{symbol}{Number(convertRaw(item.rate, "EUR")).toLocaleString()}/{rateLabel}</> : "Price N/A"}
      </div>
    </div>
  );
};

/* ======================================================
   SMALL COMPONENTS
====================================================== */
const SearchItem = ({ title, subtitle, onClick, active, dimmed }: { title: string; subtitle: string; onClick: () => void; active: boolean; dimmed: boolean }) => (
  <div onClick={onClick} style={{ ...S.searchItem, background: active ? "#fff" : dimmed ? GREY_BG : "transparent", boxShadow: active ? "0 6px 16px rgba(0,0,0,.18)" : "none", opacity: dimmed ? 0.6 : 1 }}>
    <div style={S.searchTitle}>{title}</div>
    <div style={S.searchSubtitle}>{subtitle}</div>
  </div>
);

const Divider = () => <div style={S.verticalDivider} />;

const TaxToggle = ({ taxOn, setTaxOn }: { taxOn: boolean; setTaxOn: React.Dispatch<React.SetStateAction<boolean>> }) => (
  <div style={S.taxCard} onClick={() => setTaxOn(!taxOn)}>
    <span>Show total</span>
    <div style={{ ...S.switch, background: taxOn ? GREEN_GRADIENT : "#ddd" }}>
      <div style={{ ...S.knob, left: taxOn ? 18 : 2 }} />
    </div>
  </div>
);

const AnchorModal = ({ children, full, small, right, left }: any) => (
  <div style={{ ...S.anchorModal, width: full ? "100%" : small ? 320 : "100%",
    ...(left  && { left: 0, transform: "none" }),
    ...(right && { right: 0, left: "auto", transform: "none" }),
    ...(!left && !right && { left: "50%", transform: "translateX(-50%)" }) }}>
    {children}
  </div>
);

const BottomTab = ({ icon, label, active, onClick, avatarUrl }: { icon?: React.ReactNode; label: string; active?: boolean; onClick: () => void; avatarUrl?: string }) => (
  <button onClick={onClick} style={{ ...S.tabItem, color: active ? GREEN : "#777" }}>
    {avatarUrl ? <img src={avatarUrl} alt={label} style={{ width: 22, height: 22, borderRadius: "50%", objectFit: "cover" }} /> : icon}
    <span style={{ fontSize: 11, marginTop: 4 }}>{label}</span>
  </button>
);

/* ======================================================
   STYLES
====================================================== */
const S: Record<string, React.CSSProperties> = {
  wrapper:          { background: "#fff", position: "relative", zIndex: 30 },
  searchWrapper:    { display: "flex", padding: "18px 24px", position: "relative", background: "#fff" },
  centerWrap:       { flex: 1, display: "flex", justifyContent: "center" },
  searchAnchor:     { position: "relative", display: "flex", justifyContent: "center", width: "100%" },
  searchPill:       { display: "flex", alignItems: "center", borderRadius: 999, border: "1px solid #bbf7d0", background: "#f0fdf4", position: "relative" },
  searchItem:       { padding: "14px 18px", minWidth: 150, cursor: "pointer", borderRadius: 999, transition: "all .2s" },
  searchTitle:      { fontSize: 12, fontWeight: 700, color: GREEN_DARK },
  searchSubtitle:   { fontSize: 13, color: "#777" },
  verticalDivider:  { width: 1, height: 32, background: "#bbf7d0" },
  searchBtn:        { background: GREEN_GRADIENT, border: "none", borderRadius: "50%", width: 44, height: 44, margin: 8, display: "grid", placeItems: "center" },
  anchorModal:      { position: "absolute", top: "calc(100% + 12px)", background: "#fff", borderRadius: 20, boxShadow: "0 20px 40px rgba(0,0,0,.25)", padding: 24, zIndex: 5000 },
  mobilePill:       { display: "flex", alignItems: "center", borderRadius: 999, border: "1px solid #bbf7d0", background: "#fff", width: "100%", maxWidth: 420 },
  mobileBlock:      { flex: 1, padding: "14px 16px" },
  mobileAnchor:     { margin: "12px auto", maxWidth: 420, background: "#fff", borderRadius: 20, padding: 24, boxShadow: "0 14px 36px rgba(0,0,0,.18)" },
  profileMenuBtn:   { display: "flex", alignItems: "center", gap: 8, height: 48, padding: "0 14px", minWidth: 80, borderRadius: 999, border: "1px solid #bbf7d0", background: "#fff", boxShadow: "0 2px 6px rgba(0,0,0,.12)", cursor: "pointer" },
  separator:        { height: 1, background: "#e5e5e5", width: "100%", zIndex: 1 },
  categoriesRow:    { display: "flex", alignItems: "center", padding: "12px 24px", gap: 16, width: "100%", boxSizing: "border-box", overflow: "hidden" },
  categoriesWrap:   { display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0 },
  categoriesScroll: { display: "flex", gap: 24, overflowX: "auto", overflowY: "hidden", scrollBehavior: "smooth", flex: 1, minWidth: 0, paddingBottom: 4, scrollbarWidth: "none" },
  categoryItem:     { fontSize: 12, fontWeight: 600, cursor: "pointer", background: "transparent", border: "none", padding: "6px 4px", display: "flex", flexDirection: "column", alignItems: "center", whiteSpace: "nowrap" },
  rightControls:    { display: "flex", gap: 12, flexShrink: 0 },
  navBtn:           { width: 32, height: 32, borderRadius: "50%", border: "1px solid #bbf7d0", background: "#fff", display: "grid", placeItems: "center", boxShadow: "0 2px 6px rgba(0,0,0,.15)" },
  filtersBtn:       { padding: "8px 14px", borderRadius: 14, border: "1px solid #bbf7d0", background: "#fff", fontWeight: 600 },
  taxCard:          { display: "flex", alignItems: "center", gap: 10, padding: "8px 14px", borderRadius: 14, border: "1px solid #bbf7d0", cursor: "pointer" },
  switch:           { width: 40, height: 22, borderRadius: 999, position: "relative" },
  knob:             { position: "absolute", top: 2, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: "left .2s" },
  listingsGrid:     { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 20, padding: 20 },
  mobileSheetBackdrop: { position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", zIndex: 999 },
  mobileSheet:      { position: "absolute", bottom: 0, left: 0, right: 0, background: "#fff", borderTopLeftRadius: 24, borderTopRightRadius: 24, maxHeight: "85vh", overflowY: "auto", paddingBottom: 88, boxShadow: "0 -10px 30px rgba(0,0,0,.25)" },
  mobileCard:       { background: "#fff", borderRadius: 16, margin: 12, boxShadow: "0 4px 12px rgba(0,0,0,.12)" },
  cardPreview:      { padding: 18, display: "flex", justifyContent: "space-between", cursor: "pointer" },
  sheetHeader:      { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid #eee", fontSize: 16 },
  whenToggle:       { display: "flex", gap: 6, marginBottom: 16, background: "#f0fdf4", borderRadius: 999, padding: 4, overflowX: "auto" },
  whenToggleBtn:    { flex: 1, minWidth: 52, padding: "10px 0", borderRadius: 999, border: "none", fontWeight: 700, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" },
  monthGrid:        { display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 },
  monthBtn:         { padding: "16px 0", borderRadius: 14, border: "1px solid #bbf7d0", fontWeight: 700, background: "#fff", cursor: "pointer" },
  flexBtn:          { flex: 1, padding: "14px 0", borderRadius: 999, border: "1px solid #bbf7d0", fontWeight: 700, cursor: "pointer", background: "#fff" },
  mobileSearchBtn:  { width: "100%", padding: 14, borderRadius: 999, background: GREEN_GRADIENT, color: "#fff", fontWeight: 700, border: "none" },
  mobileTabs:       { position: "fixed", bottom: 0, left: 0, right: 0, height: 64, background: "#fff", borderTop: "1px solid #bbf7d0", display: "flex", justifyContent: "space-around", alignItems: "center", zIndex: 1000 },
  tabItem:          { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "none", border: "none", padding: 0, cursor: "pointer" },
  sidebarOverlay:   { position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.6)", display: "flex", justifyContent: "flex-end", zIndex: 6000 },
  sidebar:          { width: "100%", height: "100%", background: "linear-gradient(135deg, #16a34a, white, #14532d)", boxShadow: "0 0 20px rgba(0,0,0,0.4)", padding: 24, position: "relative", display: "flex", flexDirection: "column", alignItems: "flex-start" },
  sidebarContent:   { marginTop: 70, display: "flex", flexDirection: "column", gap: 14 },
  sidebarItem:      { padding: "12px 16px", borderRadius: 12, border: "none", background: "rgba(255,255,255,0.25)", color: "#000", fontWeight: 700, textAlign: "left", cursor: "pointer", backdropFilter: "blur(3px)" },
  closeBtn:         { borderRadius: "50%", width: 36, height: 36, border: "1px solid #bbf7d0", background: "#fff" },
  closeGreenBtn:    { padding: "6px 14px", borderRadius: 999, border: "none", background: GREEN_GRADIENT, color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" },
  clearTextBtn:     { background: "transparent", border: "none", fontSize: 13, fontWeight: 600, textDecoration: "underline", cursor: "pointer", color: GREEN },
  notificationBtn:  { position: "relative", width: 48, height: 48, borderRadius: "50%", border: "1px solid #bbf7d0", background: "#fff", display: "grid", placeItems: "center", boxShadow: "0 2px 6px rgba(0,0,0,.12)", cursor: "pointer" },
  notificationBadge:{ position: "absolute", top: -4, right: -4, background: GREEN, color: "#fff", fontSize: 10, fontWeight: 900, width: 18, height: 18, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 10px rgba(0,0,0,.35)" },
  hourInput:        { width: "100%", padding: "12px 14px", borderRadius: 12, border: "2px solid #bbf7d0", fontSize: 20, fontWeight: 700, outline: "none", textAlign: "center" },
  hourHint:         { fontSize: 11, color: "#888", marginTop: 4, textAlign: "center" as const },
  quickBtn:         { padding: "8px 14px", borderRadius: 999, border: "none", fontWeight: 700, fontSize: 12, cursor: "pointer" },
};

const CS: Record<string, React.CSSProperties> = {
  card:          { position: "relative", borderRadius: 12, overflow: "hidden", backgroundColor: "#fff", cursor: "pointer" },
  imageWrapper:  { position: "relative", width: "100%", aspectRatio: "1/1" },
  image:         { width: "100%", height: "100%", objectFit: "cover" },
  chevronLeft:   { position: "absolute", top: "50%", left: 10, transform: "translateY(-50%)", backgroundColor: "rgba(0,0,0,0.4)", border: "none", borderRadius: "50%", padding: 8, cursor: "pointer", zIndex: 2 },
  chevronRight:  { position: "absolute", top: "50%", right: 10, transform: "translateY(-50%)", backgroundColor: "rgba(0,0,0,0.4)", border: "none", borderRadius: "50%", padding: 8, cursor: "pointer", zIndex: 2 },
  heartButton:   { position: "absolute", top: 10, right: 10, background: "transparent", border: "none", cursor: "pointer", zIndex: 3 },
  locationDome:  { position: "absolute", top: 0, left: 0, padding: "10px 16px", borderTopLeftRadius: 12, borderBottomRightRadius: 40, background: "linear-gradient(135deg, rgba(0,0,0,0.85), #16a34a)", color: "#fff", fontWeight: 700, fontSize: 13, zIndex: 5 },
  ratingDome:    { position: "absolute", top: 46, left: 0, padding: "6px 14px", borderTopRightRadius: 40, borderBottomRightRadius: 40, background: "linear-gradient(135deg, #bbf7d0, #16a34a, #14532d)", color: "#fff", fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center", gap: 6, zIndex: 5 },
  noRatingDome:  { position: "absolute", top: 46, left: 0, padding: "6px 14px", borderTopRightRadius: 40, borderBottomRightRadius: 40, background: "rgba(0,0,0,0.7)", color: "#ddd", fontSize: 12, zIndex: 5 },
  finalPriceDome:{ position: "absolute", top: 78, left: 0, padding: "6px 14px", borderTopRightRadius: 40, borderBottomRightRadius: 40, background: "linear-gradient(135deg, rgba(0,0,0,0.75), rgba(0,0,0,0.9))", color: "#fff", fontWeight: 900, fontSize: 14, zIndex: 5, backdropFilter: "blur(2px)" },
  priceDome:     { position: "absolute", bottom: 0, left: 0, padding: "12px 20px", borderTopRightRadius: 45, borderBottomLeftRadius: 12, background: "linear-gradient(135deg, #14532d, #16a34a, #a3e635)", color: "#fff", fontWeight: 900, fontSize: 16, zIndex: 5 },
};